import {
  MarketingActiveStatusResponseSchema,
  MarketingCampaignContactabilityResponseSchema,
  MarketingChannelResponseSchema,
  MarketingCostHeadListResponseSchema,
  MarketingCostHeadSchema,
  MarketingDailyCapResponseSchema,
  MarketingDestinationResponseSchema,
  MarketingContactabilityResponseSchema,
  MarketingLeadStatusResponseSchema,
  MarketingProfitResponseSchema,
  MarketingSpendResponseSchema,
} from '@utazzo/shared';

import { serialiseCosts } from './cost-selection';
import { monthEndDate, monthStartDate, serialiseMonths } from './month-selection';

import type {
  CreateMarketingCostHeadRequest,
  DeleteMarketingCostEntryRequest,
  MarketingCostHead,
  MarketingLeadType,
  MarketingCostHeadListResponse,
  SetManagementCostRequest,
  UpdateMarketingCostHeadRequest,
  UpsertMarketingCostEntryRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * The signed-in user's hidden Marketing Reports fields (catalog keys). The
 * screen uses it to skip the matching tiles/columns; the API has already
 * stripped the values server-side.
 */
export const getFieldVisibility = async (): Promise<string[]> => {
  const { data } = await apiClient.get<{ hidden: string[] }>('/reports/marketing/field-visibility');
  return Array.isArray(data?.hidden) ? data.hidden : [];
};

/**
 * Every read on this report is scoped by a SET of IST months, which the picker
 * lets the operator assemble with holes in it (May + July, no June). `months`
 * is what filters; `from`/`to` are the span enclosing it, sent because the API
 * prunes on the raw timestamp before applying the month set.
 *
 * `costs` is the "include these cost heads" pick: `null` sends nothing (the
 * API then includes every active head), an array sends exactly those codes,
 * and an empty array sends the `none` sentinel.
 */
const monthParams = (
  months: string[],
  costs: string[] | null = null,
  leadType: MarketingLeadType | null = null,
): {
  from: string;
  to: string;
  months: string;
  costs?: string;
  leadType?: MarketingLeadType;
} => ({
  from: monthStartDate(months[0]!),
  to: monthEndDate(months[months.length - 1]!),
  months: serialiseMonths(months),
  ...(costs === null ? {} : { costs: serialiseCosts(costs) }),
  ...(leadType === null ? {} : { leadType }),
});

export const getMarketingSpend = async (months: string[], costs: string[] | null = null) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/spend', {
    params: monthParams(months, costs),
  });
  return MarketingSpendResponseSchema.parse(data);
};

/* ------------------------------ Cost heads master ------------------------------ */

export const getMarketingCostHeads = async (): Promise<MarketingCostHeadListResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/cost-heads');
  return MarketingCostHeadListResponseSchema.parse(data);
};

export const createMarketingCostHead = async (
  body: CreateMarketingCostHeadRequest,
): Promise<MarketingCostHead> => {
  const { data } = await apiClient.post<unknown>('/reports/marketing/cost-heads', body);
  return MarketingCostHeadSchema.parse(data);
};

export const updateMarketingCostHead = async (
  id: string,
  body: UpdateMarketingCostHeadRequest,
): Promise<MarketingCostHead> => {
  const { data } = await apiClient.patch<unknown>(`/reports/marketing/cost-heads/${id}`, body);
  return MarketingCostHeadSchema.parse(data);
};

export const deleteMarketingCostHead = async (id: string): Promise<void> => {
  await apiClient.delete(`/reports/marketing/cost-heads/${id}`);
};

/** One month's figure for a head. REPLACES what is stored, never adds to it. */
export const upsertMarketingCostEntry = async (
  body: UpsertMarketingCostEntryRequest,
): Promise<void> => {
  await apiClient.put('/reports/marketing/cost-entry', body);
};

export const deleteMarketingCostEntry = async (
  body: DeleteMarketingCostEntryRequest,
): Promise<void> => {
  await apiClient.delete('/reports/marketing/cost-entry', { params: body });
};

/** Sets the retainer from `effectiveFrom` onward; later months inherit it. */
export const setManagementCost = async (body: SetManagementCostRequest): Promise<void> => {
  await apiClient.put('/reports/marketing/management-cost', body);
};

export const getMarketingQueryProfit = async (
  months: string[],
  costs: string[] | null,
  leadType: MarketingLeadType | null,
) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/query-profit', {
    params: monthParams(months, costs, leadType),
  });
  return MarketingProfitResponseSchema.parse(data);
};

export const getMarketingChannels = async (
  months: string[],
  leadType: MarketingLeadType | null,
) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/channels', {
    params: monthParams(months, null, leadType),
  });
  return MarketingChannelResponseSchema.parse(data);
};

export const getMarketingLeadStatus = async (
  months: string[],
  leadType: MarketingLeadType | null,
) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/lead-status', {
    params: monthParams(months, null, leadType),
  });
  return MarketingLeadStatusResponseSchema.parse(data);
};

export const getMarketingContactability = async (
  months: string[],
  leadType: MarketingLeadType | null,
) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/contactability', {
    params: monthParams(months, null, leadType),
  });
  return MarketingContactabilityResponseSchema.parse(data);
};

export const getMarketingCampaignContactability = async (
  months: string[],
  leadType: MarketingLeadType | null,
) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/campaign-contactability', {
    params: monthParams(months, null, leadType),
  });
  return MarketingCampaignContactabilityResponseSchema.parse(data);
};

export const getMarketingActiveStatus = async (
  months: string[],
  leadType: MarketingLeadType | null,
) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/active-status', {
    params: monthParams(months, null, leadType),
  });
  return MarketingActiveStatusResponseSchema.parse(data);
};

export const getMarketingDestinations = async (
  months: string[],
  leadType: MarketingLeadType | null,
) => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/destinations', {
    params: monthParams(months, null, leadType),
  });
  return MarketingDestinationResponseSchema.parse(data);
};

/** Today's leads-per-day caps per seller per destination; not month-bound. */
export const getMarketingDailyCaps = async () => {
  const { data } = await apiClient.get<unknown>('/reports/marketing/daily-caps');
  return MarketingDailyCapResponseSchema.parse(data);
};
